import { clsx } from "clsx";
import { Mail, Plus, Trash2 } from "lucide-react";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { TEAM_ROLES } from "../../data";
import type { InviteDraft, TeamRole } from "../../types";
import type { TeamViolations } from "../../utils/violations";
import { FieldError } from "../field-error";
import { FieldLabel } from "../field-label";
import { StepShell } from "../step-shell";

interface TeamStepProps {
	invites: InviteDraft[];
	violations?: TeamViolations;
	onAddInvite: (invite: InviteDraft) => void;
	onRemoveInvite: (id: string) => void;
	onBack: () => void;
	onContinue: () => void;
}

const ROLE_LABELS: Record<TeamRole, string> = {
	engineer: "Engenheiro",
	almoxarife: "Almoxarife",
	funcionario: "Funcionário",
	cliente: "Cliente",
};

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function TeamStep({
	invites,
	violations,
	onAddInvite,
	onRemoveInvite,
	onBack,
	onContinue,
}: TeamStepProps) {
	const [email, setEmail] = useState("");
	const [role, setRole] = useState<TeamRole>("engineer");

	const emailValid = EMAIL_PATTERN.test(email);

	function handleAdd() {
		if (!emailValid) return;
		onAddInvite({ id: crypto.randomUUID(), email, role });
		setEmail("");
	}

	return (
		<StepShell
			title="Convide sua equipe"
			description="Etapa opcional — você pode convidar pessoas agora ou mais tarde."
			onBack={onBack}
			onContinue={onContinue}
			continueLabel={invites.length > 0 ? "Continuar" : "Pular e continuar"}
		>
			<div className="team-step__section">
				<FieldLabel>Papel</FieldLabel>
				<div className="team-step__roles">
					{TEAM_ROLES.map((option) => {
						const RoleIcon = option.icon;
						const selected = role === option.id;
						return (
							<button
								key={option.id}
								type="button"
								onClick={() => setRole(option.id)}
								className={clsx("team-step__role", selected && "is-selected")}
							>
								<span className="team-step__role-icon">
									<RoleIcon />
								</span>
								<span className="team-step__role-label">{option.label}</span>
								<span className="team-step__role-desc">
									{option.description}
								</span>
							</button>
						);
					})}
				</div>
			</div>

			<div className="team-step__section">
				<FieldLabel>E-mail do convidado</FieldLabel>
				<div className="team-step__email-row">
					<Input
						type="email"
						placeholder="nome@empresa.com.br"
						value={email}
						onChange={(event) => setEmail(event.target.value)}
						onKeyDown={(event) => {
							if (event.key === "Enter") {
								event.preventDefault();
								handleAdd();
							}
						}}
					/>
					<Button type="button" onClick={handleAdd} disabled={!emailValid}>
						<Plus /> Convidar
					</Button>
				</div>
			</div>

			{invites.length > 0 && (
				<ul className="team-step__invites">
					{invites.map((invite) => {
						const inviteErrors = violations?.[invite.id];
						const inviteError = inviteErrors?.email ?? inviteErrors?.role;
						return (
							<li
								key={invite.id}
								className="team-step__invite"
								aria-invalid={!!inviteError}
							>
								<span className="team-step__invite-icon">
									<Mail />
								</span>
								<div className="team-step__invite-body">
									<p className="team-step__invite-email">{invite.email}</p>
									<p className="team-step__invite-role">
										{ROLE_LABELS[invite.role]}
									</p>
								</div>
								<Button
									type="button"
									variant="ghost"
									size="icon-sm"
									aria-label={`Remover convite de ${invite.email}`}
									onClick={() => onRemoveInvite(invite.id)}
								>
									<Trash2 />
								</Button>
								{inviteError && (
									<div className="team-step__invite-error">
										<FieldError message={inviteError} />
									</div>
								)}
							</li>
						);
					})}
				</ul>
			)}
		</StepShell>
	);
}
